import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Plus, X, AlertCircle, Loader, Film, Image as ImageIcon } from 'lucide-react';
import {
  listScheduleIntents,
  createScheduleIntent,
  updateScheduleIntent,
  deleteScheduleIntent,
} from '../../api/schedulesApi';
import { getContentStatus } from '../../api/contentApi';
import ClipBlock from './ClipBlock';
import ClipInspector from './ClipInspector';
import TVPreview from './TVPreview';
import ResolverTrace from './ResolverTrace';
import useTimelineResolver from './useTimelineResolver';
import {
  MIN_MINUTES,
  MAX_MINUTES,
  SNAP_MINUTES,
  MIN_CLIP_MINUTES,
  minuteToPx,
  formatHHMM,
  normalizeIntent,
} from './timelineGeometry';
import dashboardSocket from '../../sockets/dashboardSocket';
import './timelineEditor.css';

const LANE_HEIGHT_PX = 48;
const SHOW_RESOLVER_TRACE =
  import.meta.env.VITE_SHOW_RESOLVER_TRACE === 'true' ||
  import.meta.env.VITE_SHOW_RESOLVER_TRACE === '1';
const DEBOUNCE_MS = 500;

// Distinct hues per lane — cycled so every content item gets a stable color.
const LANE_COLORS = [
  { bg: '#2d7d5a', border: '#1f5a40' }, // green
  { bg: '#3a6ea5', border: '#2a527a' }, // blue
  { bg: '#a35a2a', border: '#7a431f' }, // orange
  { bg: '#7a3aa3', border: '#5a2a7a' }, // purple
  { bg: '#a33a5a', border: '#7a2a43' }, // pink
  { bg: '#a38a2a', border: '#7a671f' }, // mustard
  { bg: '#2a7a8a', border: '#1f5a67' }, // teal
  { bg: '#5a4a2a', border: '#3f341f' }, // brown
];

/**
 * TimelineEditor — content-per-lane editor for a single screen's day.
 *
 * Mental model:
 *   - Left rail = one lane per CONTENT item scheduled on this screen
 *     (Image 1, Image 2, Video 1, …). Lanes appear as clips are added and
 *     disappear when the last clip for that content is removed.
 *   - Each lane shows horizontal blocks marking when that content plays.
 *   - Where two or more lane blocks overlap in time, the backend loops
 *     between those contents during the overlap. Where a block sits alone,
 *     that content plays solo for its window.
 *
 * Props:
 *   - screenId, projectId  — which screen to edit (required).
 *   - windowRange {startMin,endMin}  — time window to render. Defaults to
 *     07:00–23:00 editable window. Parent (Today's Timeline) passes its
 *     Focus 2h / Business / Full / Jump window so the editor's ruler stays
 *     in sync with the outer view.
 *   - nowMinutes           — shared "now" red line. If omitted, uses live
 *     wall-clock (updated every 30s).
 *   - onClose              — parent collapse handler.
 */
export default function TimelineEditor({
  screenId,
  projectId,
  windowRange,
  nowMinutes,
  onClose,
}) {
  const activeWindow = useMemo(
    () => ({
      startMin: Number.isFinite(windowRange?.startMin) ? windowRange.startMin : MIN_MINUTES,
      endMin: Number.isFinite(windowRange?.endMin) ? windowRange.endMin : MAX_MINUTES,
    }),
    [windowRange?.startMin, windowRange?.endMin],
  );
  const windowSpan = Math.max(1, activeWindow.endMin - activeWindow.startMin);

  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(0); // in-flight request count
  const [selectedId, setSelectedId] = useState(null);
  const [library, setLibrary] = useState([]);
  const [showAddPicker, setShowAddPicker] = useState(false);

  // Local "now" fallback if parent didn't pass it.
  const [fallbackNow, setFallbackNow] = useState(() => nowMinutesOfDay());
  useEffect(() => {
    if (Number.isFinite(nowMinutes)) return undefined;
    const id = setInterval(() => setFallbackNow(nowMinutesOfDay()), 30000);
    return () => clearInterval(id);
  }, [nowMinutes]);
  const liveNow = Number.isFinite(nowMinutes) ? nowMinutes : fallbackNow;

  const tracksRef = useRef(null);
  const trackAreaRef = useRef(null); // measures only the track area (excludes left-rail label)
  const [trackAreaWidth, setTrackAreaWidth] = useState(0);
  const debounceTimers = useRef(new Map());
  const pendingPatches = useRef(new Map());
  const selfOriginTokens = useRef(new Set());

  // ── Fetch initial list ─────────────────────────────────────────────
  const reload = useCallback(async () => {
    if (!screenId || !projectId) return;
    try {
      const list = await listScheduleIntents(projectId, screenId);
      const normalized = list.map(normalizeIntent).filter(Boolean);
      setIntents(normalized);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [projectId, screenId]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  // ── Lazy-load content library ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getContentStatus(projectId);
        if (cancelled) return;
        const items = Array.isArray(res?.content) ? res.content : [];
        setLibrary(items);
      } catch (err) {
        if (!cancelled) {
          console.warn('TimelineEditor: content-status fetch failed:', err?.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // ── Socket live-collab ─────────────────────────────────────────────
  useEffect(() => {
    if (!screenId || !projectId) return undefined;
    dashboardSocket.connect();
    const handler = (payload) => {
      if (!payload) return;
      if (payload.screenId && payload.screenId !== screenId) return;
      if (payload.projectId && payload.projectId !== projectId) return;
      if (payload.intentId && selfOriginTokens.current.has(payload.intentId)) return;
      reload();
    };
    dashboardSocket.onIntentChanged(handler);
    return () => dashboardSocket.offIntentChanged(handler);
  }, [projectId, screenId, reload]);

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      pendingPatches.current.clear();
    };
  }, []);

  useEffect(() => {
    const el = trackAreaRef.current;
    if (!el) return undefined;
    const update = () => setTrackAreaWidth(el.clientWidth || 0);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, lanesCount()]);

  // Re-measure when lane count changes (first paint might have 0 lanes).
  function lanesCount() {
    return intents.length;
  }

  // ── Build lanes: one per distinct contentId scheduled on this screen ──
  // We use the first contentId of each intent as the lane key. Multi-content
  // intents (mini-loops inside a single clip) still land in one lane; their
  // "mini-loop" semantics are a power-user feature edited via the inspector.
  const lanes = useMemo(() => {
    const byContent = new Map();
    const libraryIndex = new Map();
    for (const it of library) {
      const key = it.contentId || it.contentNanoId || it.name;
      if (key && !libraryIndex.has(key)) libraryIndex.set(key, it);
      if (it.contentId && !libraryIndex.has(it.contentId)) libraryIndex.set(it.contentId, it);
      if (it.name && !libraryIndex.has(it.name)) libraryIndex.set(it.name, it);
    }
    for (const intent of intents) {
      const contentId = intent.contentIds?.[0] || null;
      const laneKey = contentId || `__clip:${intent.id}`;
      if (!byContent.has(laneKey)) byContent.set(laneKey, []);
      byContent.get(laneKey).push(intent);
    }
    const out = [];
    let idx = 0;
    for (const [laneKey, laneIntents] of byContent.entries()) {
      const libItem = libraryIndex.get(laneKey) || null;
      const earliestStart = laneIntents.reduce(
        (min, c) => (c.startMinutes < min ? c.startMinutes : min),
        Number.POSITIVE_INFINITY,
      );
      const isVirtualLane = laneKey.startsWith('__clip:');
      // Lane label priority:
      //   1. Library title (authoritative content name)
      //   2. Library name (filename fallback within library)
      //   3. contentId itself (usually a filename like "Panorama_Digital.mp4"
      //      — more informative than a generic intent name like "Base Clip")
      //   4. Intent.name (last resort; often an auto-generated label)
      const label =
        libItem?.title ||
        libItem?.name ||
        (!isVirtualLane ? laneKey : null) ||
        laneIntents[0]?.name ||
        'Unassigned clip';
      out.push({
        key: laneKey,
        contentId: isVirtualLane ? null : laneKey,
        label,
        mediaType: libItem?.mediaType || libItem?.mimeType || null,
        thumbnailUrl: libItem?.thumbnailUrl || libItem?.thumbnail || null,
        intents: laneIntents,
        color: LANE_COLORS[idx % LANE_COLORS.length],
        earliestStart,
      });
      idx++;
    }
    // Deterministic order: by earliest start time, then by label.
    out.sort((a, b) => {
      if (a.earliestStart !== b.earliestStart) return a.earliestStart - b.earliestStart;
      return String(a.label).localeCompare(String(b.label));
    });
    return out;
  }, [intents, library]);

  const selectedIntent = useMemo(
    () => intents.find((i) => i.id === selectedId) || null,
    [intents, selectedId],
  );

  // ── Patch helpers ──────────────────────────────────────────────────
  const patchLocal = useCallback((id, partial) => {
    setIntents((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const next = { ...it, ...partial };
        if ('startTime' in partial) {
          const parsed = parseIntHHMM(partial.startTime);
          if (parsed !== null) next.startMinutes = parsed;
        }
        if ('endTime' in partial) {
          const parsed = parseIntHHMM(partial.endTime);
          if (parsed !== null) next.endMinutes = parsed;
        }
        return next;
      }),
    );
  }, []);

  const scheduleServerPatch = useCallback((id, partial) => {
    const prev = pendingPatches.current.get(id) || {};
    pendingPatches.current.set(id, { ...prev, ...partial });

    const existing = debounceTimers.current.get(id);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(async () => {
      const patch = pendingPatches.current.get(id);
      pendingPatches.current.delete(id);
      debounceTimers.current.delete(id);
      if (!patch || Object.keys(patch).length === 0) return;
      setSaving((n) => n + 1);
      selfOriginTokens.current.add(id);
      setTimeout(() => selfOriginTokens.current.delete(id), 4000);
      try {
        const updated = await updateScheduleIntent(id, patch);
        const normalized = normalizeIntent(updated);
        if (normalized) {
          setIntents((prev2) => prev2.map((it) => (it.id === id ? normalized : it)));
        }
      } catch (err) {
        setError(err.message || 'Failed to save clip');
        reload();
      } finally {
        setSaving((n) => Math.max(0, n - 1));
      }
    }, DEBOUNCE_MS);
    debounceTimers.current.set(id, timer);
  }, [reload]);

  // ── Create flow ────────────────────────────────────────────────────
  // Step 1: user clicks + Add → content picker opens.
  // Step 2: user picks a content → we create an intent for that content,
  //   placing the clip at NOW (or default) for a 1h window that doesn't
  //   collide with this content's existing clips.
  const openAddPicker = useCallback(() => {
    if (!Array.isArray(library) || library.length === 0) {
      setError('Upload media first — a schedule clip needs at least one content asset.');
      return;
    }
    setShowAddPicker(true);
  }, [library]);

  const handlePickContent = useCallback(
    async (pickedContentId) => {
      setShowAddPicker(false);
      if (!pickedContentId || !screenId || !projectId) return;

      // Seed around NOW (or windowStart if now isn't in the visible range).
      const seedCenter = Number.isFinite(liveNow)
        ? Math.max(activeWindow.startMin, Math.min(activeWindow.endMin - 30, liveNow))
        : activeWindow.startMin + 60;
      let startMin = Math.max(activeWindow.startMin, seedCenter - 15);
      let endMin = Math.min(activeWindow.endMin, startMin + 60);
      if (endMin <= startMin + MIN_CLIP_MINUTES) {
        endMin = startMin + 60;
      }

      // Nudge forward if this content already has a clip covering that range.
      const sameContent = intents.filter(
        (it) => (it.contentIds?.[0] || null) === pickedContentId,
      );
      let attempts = 0;
      while (
        sameContent.some((it) => startMin < it.endMinutes && endMin > it.startMinutes) &&
        attempts < 48
      ) {
        startMin += 30;
        endMin += 30;
        if (endMin > MAX_MINUTES) break;
        attempts++;
      }

      const libraryItem = library.find(
        (l) => l.contentId === pickedContentId || l.contentNanoId === pickedContentId || l.name === pickedContentId,
      );
      const displayName =
        libraryItem?.title || libraryItem?.name || 'New clip';

      const body = {
        screenId,
        name: displayName,
        contentIds: [pickedContentId],
        track: 1,
        startTime: formatHHMM(startMin),
        endTime: formatHHMM(endMin),
        overlapPolicy: 'merge',
      };
      setSaving((n) => n + 1);
      try {
        const created = await createScheduleIntent(projectId, body);
        const normalized = normalizeIntent(created);
        if (normalized) {
          selfOriginTokens.current.add(normalized.id);
          setTimeout(() => selfOriginTokens.current.delete(normalized.id), 4000);
          setIntents((prev) => [...prev, normalized]);
          setSelectedId(normalized.id);
        } else {
          await reload();
        }
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to create clip');
      } finally {
        setSaving((n) => Math.max(0, n - 1));
      }
    },
    [activeWindow, intents, library, liveNow, projectId, reload, screenId],
  );

  // ── Delete ─────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (id) => {
      if (!id) return;
      const snapshot = intents;
      setIntents((prev) => prev.filter((it) => it.id !== id));
      if (selectedId === id) setSelectedId(null);
      setSaving((n) => n + 1);
      selfOriginTokens.current.add(id);
      setTimeout(() => selfOriginTokens.current.delete(id), 4000);
      try {
        await deleteScheduleIntent(id);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to delete clip');
        setIntents(snapshot);
      } finally {
        setSaving((n) => Math.max(0, n - 1));
      }
    },
    [intents, selectedId],
  );

  // ── Keyboard shortcuts ─────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const inField = tag === 'input' || tag === 'textarea' || tag === 'select';
      if (e.key === 'Escape') {
        if (showAddPicker) {
          setShowAddPicker(false);
          e.preventDefault();
          return;
        }
        if (selectedId && !inField) {
          setSelectedId(null);
          e.preventDefault();
          return;
        }
        onClose?.();
        return;
      }
      if (inField) return;
      if (!selectedIntent) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDelete(selectedIntent.id);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const delta = (e.key === 'ArrowRight' ? 1 : -1) * SNAP_MINUTES;
        const len = selectedIntent.endMinutes - selectedIntent.startMinutes;
        const newStart = Math.max(
          MIN_MINUTES,
          Math.min(MAX_MINUTES - len, selectedIntent.startMinutes + delta),
        );
        const newEnd = newStart + len;
        const patch = {
          startTime: formatHHMM(newStart),
          endTime: formatHHMM(newEnd),
        };
        patchLocal(selectedIntent.id, {
          ...patch,
          startMinutes: newStart,
          endMinutes: newEnd,
        });
        scheduleServerPatch(selectedIntent.id, patch);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    handleDelete,
    onClose,
    patchLocal,
    scheduleServerPatch,
    selectedId,
    selectedIntent,
    showAddPicker,
  ]);

  const resolverResult = useTimelineResolver(intents, liveNow);

  // Double-click an empty lane-track area → open the content picker so the
  // user can add a new clip. Single-click just deselects (doesn't trip on
  // accidental taps during panning).
  const handleTracksBackgroundClick = (e) => {
    const isEmpty =
      e.currentTarget === e.target ||
      e.target.classList?.contains('timeline-editor__lane') ||
      e.target.classList?.contains('timeline-editor__lane-track');
    if (!isEmpty) return;
    if (e.detail >= 2) {
      openAddPicker();
      return;
    }
    setSelectedId(null);
  };

  // Ruler ticks — dynamic, keyed to activeWindow. Every 15 min grid line,
  // hourly labels. If the visible window is <4h, label every 30 min.
  const rulerTicks = useMemo(() => {
    const out = [];
    const step = windowSpan <= 240 ? 15 : 30;
    const labelEvery = windowSpan <= 240 ? 30 : 60;
    // Align to a sensible grid start.
    const firstTick = Math.ceil(activeWindow.startMin / step) * step;
    for (let m = firstTick; m <= activeWindow.endMin; m += step) {
      out.push({
        minute: m,
        label: m % labelEvery === 0 ? formatHHMM(m) : null,
        major: m % labelEvery === 0,
      });
    }
    return out;
  }, [activeWindow, windowSpan]);

  if (!projectId || !screenId) {
    return (
      <div className="timeline-editor timeline-editor--error">
        <AlertCircle size={16} />
        <span>Missing project or screen context.</span>
      </div>
    );
  }

  return (
    <div className="timeline-editor" role="region" aria-label="Timeline editor">
      {/* Saving / loading indicator floats top-right so it doesn't steal the
          left column's real estate. Close button lives here too. */}
      {(saving > 0 || loading) && (
        <span className="timeline-editor__status-pill" aria-live="polite">
          <Loader size={12} className="timeline-editor__spin" />
          {saving > 0 ? 'Saving…' : 'Loading…'}
        </span>
      )}
      <button
        type="button"
        className="timeline-editor__close-floating"
        onClick={() => onClose?.()}
        aria-label="Close editor"
      >
        <X size={14} />
      </button>

      {error && (
        <div className="timeline-editor__error">
          <AlertCircle size={14} />
          <span>{error}</span>
          <button
            type="button"
            className="timeline-editor__error-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="timeline-editor__body">
        <div className="timeline-editor__timeline">
          {/* Ruler row: [Schedule header 180px | ticks 1fr] — matches outer
              row's screen-label + timeline axis shape. The "Schedule" text
              sits in the same column as "Convrse HO Standee" above. */}
          <div className="timeline-editor__ruler-row">
            <div className="timeline-editor__ruler-label-spacer">Schedule</div>
            <div
              ref={trackAreaRef}
              className="timeline-editor__ruler-ticks"
            >
              <Ruler ticks={rulerTicks} windowRange={activeWindow} />
            </div>
          </div>

          <div
            ref={tracksRef}
            className="timeline-editor__tracks"
            onClick={handleTracksBackgroundClick}
            style={{
              minHeight: `${Math.max(lanes.length, 1) * LANE_HEIGHT_PX + 16}px`,
            }}
          >
            {lanes.length === 0 ? (
              <EmptyState onAdd={openAddPicker} disabled={library.length === 0} />
            ) : (
              lanes.map((lane, laneIdx) => (
                <ContentLane
                  key={lane.key}
                  lane={lane}
                  laneIdx={laneIdx}
                  laneHeightPx={LANE_HEIGHT_PX}
                  windowRange={activeWindow}
                  containerWidth={trackAreaWidth}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onLocalPatch={patchLocal}
                  onCommit={scheduleServerPatch}
                />
              ))
            )}
            <PlayheadAbsolute
              minutes={liveNow}
              windowRange={activeWindow}
              trackAreaWidth={trackAreaWidth}
              labelWidth={180}
            />
          </div>
        </div>

        {selectedIntent && (
          <div className="timeline-editor__sidebar">
            <ClipInspector
              intent={selectedIntent}
              library={library}
              onPatch={(id, partial) => {
                patchLocal(id, partial);
                scheduleServerPatch(id, partial);
              }}
              onDelete={handleDelete}
            />
          </div>
        )}
      </div>

      {/* ── Footer: live "Now playing" preview + (dev) resolver trace ── */}
      <div
        className={[
          'timeline-editor__footer',
          SHOW_RESOLVER_TRACE && 'timeline-editor__footer--with-trace',
        ].filter(Boolean).join(' ')}
      >
        <div className="timeline-editor__footer-left">
          <TVPreview playheadMinutes={liveNow} result={resolverResult} />
        </div>
        {SHOW_RESOLVER_TRACE && <ResolverTrace result={resolverResult} />}
      </div>

      {/* ── Content picker modal ───────────────────────────────────── */}
      {showAddPicker && (
        <ContentPicker
          library={library}
          onCancel={() => setShowAddPicker(false)}
          onPick={handlePickContent}
        />
      )}
    </div>
  );
}

/* ── Lane — one row per distinct content item ─────────────────────── */

function ContentLane({
  lane,
  laneIdx,
  laneHeightPx,
  windowRange,
  containerWidth,
  selectedId,
  onSelect,
  onLocalPatch,
  onCommit,
}) {
  const trackRef = useRef(null);
  return (
    <div
      className="timeline-editor__lane"
      data-lane-idx={laneIdx}
      style={{ height: `${laneHeightPx}px` }}
    >
      <span className="timeline-editor__lane-label" title={lane.label}>
        <LaneIcon mediaType={lane.mediaType} />
        <span className="timeline-editor__lane-label-text">{lane.label}</span>
      </span>
      <div ref={trackRef} className="timeline-editor__lane-track">
        {lane.intents.map((it) => (
          <ClipBlock
            key={it.id}
            intent={it}
            laneIndex={laneIdx}
            laneHeightPx={laneHeightPx}
            windowRange={windowRange}
            containerWidth={containerWidth}
            containerRef={trackRef}
            displayName={lane.label}
            color={lane.color}
            selected={it.id === selectedId}
            onSelect={onSelect}
            onLocalPatch={onLocalPatch}
            onCommit={onCommit}
          />
        ))}
      </div>
    </div>
  );
}

function LaneIcon({ mediaType }) {
  const mt = String(mediaType || '').toLowerCase();
  if (mt.includes('video')) {
    return <Film size={12} aria-hidden="true" />;
  }
  return <ImageIcon size={12} aria-hidden="true" />;
}

/* ── Ruler — hour/half-hour ticks, dynamic window ────────────────── */

function Ruler({ ticks, windowRange }) {
  return (
    <div className="timeline-editor__ruler" aria-hidden="true">
      {ticks.map((t) => {
        const leftPct = ((t.minute - windowRange.startMin) / Math.max(1, windowRange.endMin - windowRange.startMin)) * 100;
        return (
          <React.Fragment key={t.minute}>
            <div
              className={[
                'timeline-editor__ruler-tick',
                t.major && 'timeline-editor__ruler-tick--major',
              ].filter(Boolean).join(' ')}
              style={{ left: `${leftPct}%` }}
            />
            {t.label && (
              <div
                className="timeline-editor__ruler-label"
                style={{ left: `${leftPct}%` }}
              >
                {t.label}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Playhead — shared "now" red line, offset past the label column ── */

function PlayheadAbsolute({ minutes, windowRange, trackAreaWidth, labelWidth }) {
  if (
    !Number.isFinite(minutes) ||
    minutes < windowRange.startMin ||
    minutes > windowRange.endMin
  ) {
    return null;
  }
  const leftPx = (labelWidth || 0) + minuteToPx(minutes, trackAreaWidth || 0, windowRange);
  return (
    <div
      className="timeline-editor__playhead"
      style={{ left: `${leftPx}px` }}
      aria-hidden="true"
    />
  );
}

/* ── Empty state (no clips on this screen yet) ────────────────────── */

function EmptyState({ onAdd, disabled }) {
  if (disabled) {
    return (
      <div className="timeline-editor__empty timeline-editor__empty--disabled">
        <p>Upload media first to schedule it on this screen.</p>
      </div>
    );
  }
  // "Ghost lane" — the left column shows the + affordance aligned with
  // where real content names will land, so the user can see the layout
  // forming even before any clip exists. The whole row is clickable.
  return (
    <div
      className="timeline-editor__empty-lane"
      role="button"
      tabIndex={0}
      onClick={onAdd}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAdd();
        }
      }}
    >
      <div className="timeline-editor__empty-lane-label">
        <Plus size={14} />
        <span>Add content</span>
      </div>
      <div className="timeline-editor__empty-lane-track">
        <span>
          Click here or double-click the timeline to schedule your first clip.
        </span>
      </div>
    </div>
  );
}

/* ── Content picker modal ─────────────────────────────────────────── */

function ContentPicker({ library, onCancel, onPick }) {
  const [filter, setFilter] = useState('');
  const normalized = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return library
      .map((it) => ({
        id: it.contentId || it.contentNanoId || it.name,
        label: it.title || it.name || it.filename || 'Untitled',
        thumbnailUrl: it.thumbnailUrl || it.thumbnail || null,
        mediaType: it.mediaType || it.mimeType || null,
      }))
      .filter((it) => it.id)
      .filter((it) => !f || it.label.toLowerCase().includes(f));
  }, [library, filter]);

  return (
    <div
      className="timeline-editor__picker-backdrop"
      onClick={onCancel}
    >
      <div
        className="timeline-editor__picker"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Pick content to schedule"
      >
        <div className="timeline-editor__picker-header">
          <h3>Schedule content</h3>
          <button
            type="button"
            className="timeline-editor__picker-close"
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X size={14} />
          </button>
        </div>
        <input
          type="text"
          className="timeline-editor__picker-search"
          placeholder="Search media…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoFocus
        />
        <div className="timeline-editor__picker-grid">
          {normalized.length === 0 ? (
            <div className="timeline-editor__picker-empty">No matching content.</div>
          ) : (
            normalized.map((it) => (
              <button
                key={it.id}
                type="button"
                className="timeline-editor__picker-item"
                onClick={() => onPick(it.id)}
              >
                {it.thumbnailUrl ? (
                  <img src={it.thumbnailUrl} alt="" className="timeline-editor__picker-thumb" />
                ) : (
                  <div className="timeline-editor__picker-thumb timeline-editor__picker-thumb--empty">
                    <LaneIcon mediaType={it.mediaType} />
                  </div>
                )}
                <span className="timeline-editor__picker-label">{it.label}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */

function parseIntHHMM(s) {
  if (typeof s !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
  return hours * 60 + mins;
}

function nowMinutesOfDay() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

export const EDITOR_MIN_CLIP_MINUTES = MIN_CLIP_MINUTES;
